import type { Json } from '@deltasuite/supabase-client';
import { useState } from 'react';
import { createCampaign } from '../api/campaigns';
import {
  EMPTY_FILTER,
  FilterPanel,
  toContactFilter,
  type FilterPanelValue,
} from '../sprints/FilterPanel';

interface Props {
  onCreated: () => void;
  onCancel: () => void;
}

const STUB_TEMPLATES = [
  { id: 'tpl-ember', label: 'Ember Bronze Brochure' },
  { id: 'tpl-as1428', label: 'AS.1428 Brochure' },
  { id: 'tpl-spec', label: 'Specifier Email — Commercial' },
  { id: 'tpl-handle', label: 'Pull Handle Campaign 2025' },
] as const;

export function CampaignCreate({ onCreated, onCancel }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [filter, setFilter] = useState<FilterPanelValue>(EMPTY_FILTER);
  const [scriptLocked, setScriptLocked] = useState(false);
  const [script, setScript] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [pointGoal, setPointGoal] = useState<number>(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const audience = toContactFilter(filter);
      await createCampaign({
        name: name.trim(),
        description: description.trim() || undefined,
        starts_at: startsAt || undefined,
        ends_at: endsAt || undefined,
        audience_filter: hasAnyFilter(audience) ? (audience as unknown as Json) : undefined,
        // The "locked" flag is an admin policy — encoded into the script string
        // until the schema gains a dedicated column.
        script: script.trim()
          ? scriptLocked
            ? `[locked]\n\n${script.trim()}`
            : script.trim()
          : undefined,
        email_template_id: emailTemplateId || undefined,
        point_goal: pointGoal,
      });
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="px-8 py-10">
      <div className="mx-auto max-w-2xl">
        <header className="flex items-baseline justify-between">
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
            New campaign
          </h1>
          <button
            type="button"
            onClick={onCancel}
            className="font-mono text-xs text-graphite-500 hover:text-graphite-900"
          >
            ← back
          </button>
        </header>

        <div className="mt-8 space-y-8">
          <Section label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ember Bronze Specifier Sprint — Q2"
              className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </Section>

          <Section label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What this is, who it's for, and what good looks like."
              className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </Section>

          <div className="grid grid-cols-2 gap-4">
            <Section label="Starts">
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
              />
            </Section>
            <Section label="Ends">
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
              />
            </Section>
          </div>

          <Section label="Audience filter">
            <FilterPanel value={filter} onChange={setFilter} />
          </Section>

          <Section label="Script">
            <label className="flex items-center gap-2 font-sans text-sm text-graphite-700">
              <input
                type="checkbox"
                checked={scriptLocked}
                onChange={(e) => setScriptLocked(e.target.checked)}
                className="size-4 accent-signal-amber"
              />
              Lock script — recipients can't edit
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={6}
              placeholder="Hi [contact name], it's [your name]…"
              className="mt-2 w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            />
          </Section>

          <Section label="Email template (VM&E)">
            <select
              value={emailTemplateId}
              onChange={(e) => setEmailTemplateId(e.target.value)}
              className="w-full rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
            >
              <option value="">— none —</option>
              {STUB_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="mt-1 font-mono text-[0.7rem] text-graphite-500">
              Stub list. Real templates load from Zoho once auth is wired.
            </p>
          </Section>

          <Section label="Point goal">
            <div className="flex w-32 items-baseline gap-2 rounded-md border border-graphite-300 bg-surface px-3 py-2">
              <input
                type="number"
                min={1}
                max={10000}
                value={pointGoal}
                onChange={(e) => setPointGoal(parseInt(e.target.value, 10) || 30)}
                className="w-full bg-transparent font-sans text-xl font-semibold text-graphite-900 outline-none"
              />
              <span className="font-mono text-xs text-graphite-500">pts</span>
            </div>
          </Section>
        </div>

        <div className="mt-10 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-4 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!name.trim() || submitting}
            className="rounded-md bg-signal-amber px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-signal-amber-hover disabled:opacity-40"
          >
            {submitting ? 'Creating…' : 'Create campaign'}
          </button>
        </div>

        {error ? <p className="mt-4 font-mono text-xs text-signal-amber">{error}</p> : null}
      </div>
    </main>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
        {label}
      </h3>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function hasAnyFilter(f: ReturnType<typeof toContactFilter>): boolean {
  return Boolean(
    f.state?.length ||
    f.companyType?.length ||
    f.hasMobile ||
    f.lastActivityAfter ||
    f.lastActivityBefore,
  );
}
